import { ActiveServicePreview, ServiceContainer } from '@shared/components';
import { OnboardingSaveHighwaysDto, DayOfWeek } from '@interfaces/models';
import { HighwaySectionDto } from '@interfaces/types';
import { Highway } from '@ui/components/icons';
import { useTranslation } from 'react-i18next';
import { Separator } from '@ui/components';
import { useEffect, FC } from 'react';

import { HighwaysConfigurationForm } from './highways-configuration-form/highways-configuration-form';
import { useHighwaysConfiguration } from './use-highways-configuration';

interface HighwaysConfigurationProps {
  onConfigurationChange: (config: OnboardingSaveHighwaysDto | null) => void;
  setIsEditing?: (isEditing: boolean) => void;
  initialConfiguration?: OnboardingSaveHighwaysDto | null;
  highwaySections: HighwaySectionDto[];
  defaultSelected?: boolean;
  toggleDisabled?: boolean;
  canNotEdit?: boolean;
  className?: string;
  days: DayOfWeek[];
}

export const HighwaysConfiguration: FC<HighwaysConfigurationProps> = ({
  onConfigurationChange,
  initialConfiguration,
  highwaySections,
  defaultSelected,
  toggleDisabled,
  setIsEditing,
  canNotEdit,
  className,
  days,
}) => {
  const { t } = useTranslation();

  const {
    prepareHighwaysPreviewData,
    createDefaultConfiguration,
    handleRevertChanges,
    handleActiveChange,
    isEntityActive,
    configuration,
    handleEdit,
    handleSave,
    isEditing,
  } = useHighwaysConfiguration({
    onUpdate: onConfigurationChange,
    initialConfiguration,
    defaultSelected,
    highwaySections,
    days,
  });

  useEffect(() => setIsEditing?.(isEditing), [isEditing, setIsEditing]);

  const defaultHighwaysConfiguration = createDefaultConfiguration();
  const highwaysConfigurationPreviewData = prepareHighwaysPreviewData(
    configuration ?? defaultHighwaysConfiguration,
  );

  return (
    <ServiceContainer
      title={t('COMPANY_POLICY.HIGHWAYS_CONFIGURATION_TITLE')}
      previewData={highwaysConfigurationPreviewData}
      onActiveChange={handleActiveChange}
      toggleDisabled={toggleDisabled}
      active={isEntityActive}
      className={className}
      icon={<Highway />}
    >
      <>
        {(isEditing || isEntityActive) && <Separator className="mt-4 mb-4" />}

        <div>
          {isEntityActive && !isEditing && (
            <ActiveServicePreview
              data={highwaysConfigurationPreviewData}
              canNotEdit={canNotEdit}
              onEdit={handleEdit}
            />
          )}

          {isEditing && (
            <div>
              <HighwaysConfigurationForm
                defaultHighwaysConfiguration={configuration ?? defaultHighwaysConfiguration}
                onRevertChanges={handleRevertChanges}
                highwaySections={highwaySections}
                onSubmit={handleSave}
                days={days}
              />
            </div>
          )}
        </div>
      </>
    </ServiceContainer>
  );
};
